import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';

import { ABSENCE_TEXT, formatFileSize, formatIsoDate, formatIsoDateRange } from '../oasis-display-format';
import type { DisplayBlock, DisplayFileDownloadError, DisplayFileRef, DisplayValue } from '../oasis-display.types';

/** Distinguishes one renderer instance's generated element ids from another's. */
let instanceCounter = 0;

/**
 * Renders the INSIDE of one application section from its projected blocks.
 *
 * WHY IT LIVES HERE, AND WHY IT STOPS WHERE IT DOES
 *
 * This is the first component in `shared-resources`, so it sets the convention.
 * Two boundaries are deliberate:
 *
 * 1. NO ng-bootstrap. The accordion stays in each host page. That is what keeps
 *    this library on `@angular/common` and `@angular/core` alone — both already
 *    peer dependencies — so adding a shared view costs no new dependency. The two
 *    hosts also want different chrome around the same content (the admin side will
 *    grow review actions and role gating that have no business on the applicant
 *    page), and a component that rendered the accordion too would have to grow
 *    options for both.
 *
 * 2. NO DATA ACCESS AND NO POLICY. It takes `DisplayBlock[]` and renders it.
 *    Which sections exist, who may see them, and why one is empty are all decided
 *    by `projectApplication` before anything reaches here. This component cannot
 *    leak a section it was not given.
 *
 *    The download control keeps that boundary. Fetching a file needs a signed URL,
 *    and the two hosts call different APIs to get one, so this component only
 *    reports the intent through `fileDownloadRequested` and renders whatever
 *    progress or failure the host passes back. It never sees a URL, and
 *    `filesDownloadable` is the host's decision, not its own.
 *
 *    Progress and failure arrive as inputs rather than fields this component sets,
 *    which keeps it a pure function of its inputs and `OnPush` correct.
 *
 * HEADING LEVELS ARE AN INPUT, not a guess. The applicant page wraps sections in
 * `<h3>` and the admin page uses `<h2>`, so a hardcoded level would produce a
 * broken heading hierarchy on one of them — itself an accessibility fault, and a
 * silent one. Sub-headings are emitted with `role="heading"` plus an explicit
 * `aria-level` rather than four template branches for h2/h3/h4/h5, which is the
 * standard ARIA escape hatch for exactly this problem.
 *
 * STYLES: essentially none. Both apps load the same vendored Bootstrap, so layout
 * comes from Bootstrap utility classes. The one exception is `white-space:
 * pre-wrap` for long free text, which Bootstrap has no utility for and which
 * matters — without it, an applicant's carefully paragraphed essay collapses into
 * a single run of text.
 */
@Component({
	selector: 'oasis-section-body',
	standalone: true,
	imports: [DatePipe, NgTemplateOutlet],
	templateUrl: './oasis-section-body.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styles: [
		`
			.oasis-longtext {
				white-space: pre-wrap;
				word-break: break-word;
			}
		`,
	],
})
export class OasisSectionBodyComponent {
	readonly blocks = input.required<readonly DisplayBlock[]>();

	/**
	 * Heading level of the element this body sits under, so nested headings can
	 * continue the hierarchy instead of restarting it. The applicant page's section
	 * headers are `<h3>`, so 3 is the default there.
	 */
	readonly headingLevel = input<number>(3);

	/**
	 * Whether uploaded documents get a view/download control. Off by default, so a
	 * host that has not wired `fileDownloadRequested` cannot render a button that
	 * does nothing.
	 */
	readonly filesDownloadable = input<boolean>(false);

	/** `s3Key` of the file being fetched, or null. Set by the host. */
	readonly downloadingKey = input<string | null>(null);

	/** The most recent per-file failure, or null. Set by the host. */
	readonly downloadError = input<DisplayFileDownloadError | null>(null);

	/** The user asked to open this file. The host resolves and opens it. */
	readonly fileDownloadRequested = output<DisplayFileRef>();

	private readonly instanceId = `oasis-body-${++instanceCounter}`;

	/** Level for a block heading: one below the section header. */
	blockLevel(): number {
		return this.headingLevel() + 1;
	}

	/** Level for an entry title: one below a block heading. */
	entryLevel(): number {
		return this.headingLevel() + 2;
	}

	// ─── Value rendering helpers ───────────────────────────────────────────────

	/**
	 * Wording for an absent value.
	 *
	 * "Not applicable" and "Not provided" are different statements and must not be
	 * collapsed: the first says the question never applied given an earlier answer,
	 * the second says it was asked and left blank.
	 */
	absenceText(value: DisplayValue): string {
		return value.kind === 'empty' ? ABSENCE_TEXT[value.reason] : '';
	}

	/** Dates render exactly as stored — see `formatIsoDate` for why. */
	isoDate(iso: string): string {
		return formatIsoDate(iso);
	}

	isoRange(fromIso: string, toIso: string, ongoing: boolean | undefined): string {
		return formatIsoDateRange(fromIso, toIso, ongoing ?? false);
	}

	fileSize(bytes: number): string {
		return formatFileSize(bytes);
	}

	// ─── Document download ─────────────────────────────────────────────────────

	/** Whether this specific file is the one currently in flight. */
	isDownloading(file: DisplayFileRef): boolean {
		return this.downloadingKey() === file.s3Key;
	}

	/** The failure message for this file, or null if the last failure was elsewhere. */
	errorFor(file: DisplayFileRef): string | null {
		const err = this.downloadError();
		return err && err.s3Key === file.s3Key ? err.message : null;
	}

	/**
	 * Accessible name for a file's download control. Every row's visible text reads
	 * "View/Download", so the filename goes in the accessible name to tell them apart
	 * out of context.
	 */
	downloadLabel(file: DisplayFileRef): string {
		return this.isDownloading(file) ? `Preparing ${file.filename}` : `View or download ${file.filename}`;
	}

	/**
	 * Id of a row's error element, for `aria-describedby`. Uses the index because an
	 * `s3Key` contains characters that are not valid in an id, and the instance prefix
	 * keeps ids unique when a page renders several sections.
	 */
	errorId(index: number): string {
		return `${this.instanceId}-file-${index}-error`;
	}

	/**
	 * Summary line for a checklist.
	 *
	 * The zero case gets its own wording rather than "0 of 35 selected", because
	 * that reading is the whole point of the section being present at all: the
	 * applicant considered these options and claimed none of them. A bare zero
	 * looks like a rendering fault.
	 */
	checklistSummary(selectedCount: number, totalAvailable: number): string {
		if (selectedCount === 0) return `None of the ${totalAvailable} options were selected.`;
		return `${selectedCount} of ${totalAvailable} selected`;
	}
}
